<div class="page">
  @if(isReadOnly) {
    <h1 class="page-title-no-border">
      #{{orderId}} - {{projectType}} - {{projectDescription}} - {{ 'budget-inclusion.budgetDetails' | translate }}
    </h1>
  } @else {
    <h1 class="page-title-no-border">
      #{{orderId}} - {{projectType}} - {{projectDescription}} - {{ 'budget-inclusion.orderTitleDetailsNewBudget' | translate }}
    </h1>
  }
  <div fxLayout="column">
    <form
      fxFlex="100"
      fxLayout="row"
      fxLayoutGap="20px"
      fxLayoutAlign="space-between center"
      [formGroup]="budgetForm"
    >
      <mat-form-field fxFlex="100" appearance="outline" class="field-readonly">
        <mat-label>{{ 'budget-inclusion.supplier' | translate }}</mat-label>
        <input formControlName="supplier" name="supplier" matInput readonly />
      </mat-form-field>
    </form>

    <h2 class="subtitle mg-b-22">{{ 'budget-inclusion.items' | translate }}</h2>
    <div fxLayout="column" fxLayoutGap="20px" class="items-container mg-b-40">
      @for (orderItem of orderItems; track orderItem; let i = $index) {
        <div fxLayout="row" fxLayoutAlign="space-between center" class="item-row">
          <h2 class="item-label">
            {{orderItem.item?.id}} - {{ orderItem.item?.partNumber }} - {{ orderItem.item?.description }} {{
              orderItem.item?.manufacturer
            }}
          </h2>
          <form [formGroup]="orderItemForms[i]" fxLayoutGap="10px" fxLayoutAlign="end">
            <mat-form-field appearance="outline" class="field-readonly">
              <mat-label>{{ 'budget-inclusion.itemTotalQty' | translate }}</mat-label>
              <input
                matInput
                formControlName="requestedQuantity"
                [value]="orderItem.requestedQuantity"
                readonly
                [disabled]="true"
              />
            </mat-form-field>
            @if(!isReadOnly) {
              <mat-form-field appearance="outline">
                <mat-label>{{ 'budget-inclusion.availableQty' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="availableQuantity"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  min="0"
                  step="1"
                  (keydown)="allowOnlyDigits($event)"
                  (blur)="onAvailableQtyBlur(i)"
                />
                 @if(orderItemForms[i].get('availableQuantity')?.hasError('max')){
                  <mat-error class="available-quantity-error">
                    {{ 'budget.error.availableQuantityInvalid' | translate }}
                  </mat-error>
                 }
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>{{ 'budget-inclusion.unitValue' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="unitValue"
                  currencyMask
                  [options]="{
                    prefix: '',
                    thousands: '.',
                    decimal: ',',
                    align: 'right',
                    allowNegative: false
                  }"
                  (keydown)="blockInvalidCharacters($event)"
                  (blur)="onCurrencyBlur(orderItemForms[i], 'unitValue')"
                  type="text"
                />
              </mat-form-field>
            } @else {
              <mat-form-field appearance="outline">
                <mat-label>{{ 'budget-inclusion.availableQty' | translate }}</mat-label>
                <input matInput formControlName="availableQuantity" [value]="orderItem.availableQuantity" readonly />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>{{ 'budget-inclusion.unitValue' | translate }}</mat-label>
                <input matInput formControlName="unitValue" [value]="orderItem.unitValue" readonly />
              </mat-form-field>
            }
            <mat-form-field appearance="outline">
              <mat-label>{{ 'budget-inclusion.totalValue' | translate }}</mat-label>
              <input
                matInput
                formControlName="totalValue"
                readonly
                currencyMask
                [options]="{
                  prefix: '',
                  thousands: '.',
                  decimal: ',',
                  align: 'right',
                  allowNegative: false
                }"
              />
            </mat-form-field>
          </form>
        </div>
      }
    </div>

    <h2 class="subtitle mg-b-22">{{ 'budget-inclusion.additionalCosts' | translate }}</h2>
    @if(!hasAdditionalCosts() && isReadOnly) {
      <h2 class="text">{{ 'budget-inclusion.noAdditionalCosts' | translate }}</h2>
    }
    @if(!isReadOnly) {
      <form
        fxFlex="100"
        fxLayout="row"
        fxLayoutGap="20px"
        fxLayoutAlign="space-between center"
        [formGroup]="additionalCostsForm"
      >
        <mat-form-field fxFlex="33.33" appearance="outline">
          <mat-label>{{ 'budget-inclusion.costType' | translate }}</mat-label>
          <mat-select formControlName="costType" (selectionChange)="validateAdditionalCosts()">
            @for (costTypeOption of costTypeOptions; track costTypeOption) {
              <mat-option [value]="costTypeOption">{{ costTypeOption.costType }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field fxFlex="33.33" appearance="outline">
          <mat-label>{{ 'budget-inclusion.description' | translate }}</mat-label>
          <input matInput formControlName="description" />
        </mat-form-field>
        <mat-form-field fxFlex="33.33" appearance="outline">
          <mat-label>{{ 'budget-inclusion.costValue' | translate }}</mat-label>
          <input
            matInput
            formControlName="value"
            currencyMask
            [options]="{
              prefix: '',
              thousands: '.',
              decimal: ',',
              align: 'right',
              allowNegative: false
            }"
            (keydown)="blockInvalidCharacters($event)"
            (blur)="onCurrencyBlur(additionalCostsForm, 'value')"
            type="text"
          />
        </mat-form-field>
        <button
          type="button"
          mat-flat-button
          color="primary"
          class="add-button mg-b-22"
          [disabled]="!(additionalCostsForm.valid && valueNumeric > 0)"
          (click)="isEdit ? updateAdditionalCost() : addAdditionalCost()"
        >
          {{ (isEdit ? "button.update" : "button.add") | translate }}
        </button>
      </form>
    }
    @if(!isReadOnly) {
      <table mat-table [dataSource]="dataSource">
        <ng-container matColumnDef="costType">
          <th class="table-header" mat-header-cell *matHeaderCellDef style="width: 40%;">
            {{ 'budget-inclusion.costType' | translate }}
          </th>
          <td mat-cell *matCellDef="let element">
            {{ element.costType.costType }}
          </td>
        </ng-container>

        <ng-container matColumnDef="description">
          <th class="table-header" mat-header-cell *matHeaderCellDef style="width: 20%;">
            {{ 'budget-inclusion.description' | translate }}
          </th>
          <td mat-cell *matCellDef="let element">
            {{ element.description || '-' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="value">
          <th class="table-header" mat-header-cell *matHeaderCellDef style="width: 20%;">
            {{ 'budget-inclusion.costValue' | translate }}
          </th>
          <td mat-cell *matCellDef="let element">
            {{ element.value | number:'1.2-2' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="action">
          <th class="table-header action" mat-header-cell *matHeaderCellDef style="width: 40%;">
            {{ 'budget-inclusion.action' | translate }}
          </th>
          <td mat-cell *matCellDef="let element" class="action">
            <button mat-icon-button (click)="editAdditionalCost(element)">
              <mat-icon class="material-symbols-outlined" matTooltip="{{'button.edit-tooltip' | translate}}"
                style="color: black;">
                edit_square
              </mat-icon>
            </button>
            <button mat-icon-button (click)="deleteAdditionalCost(element)">
              <mat-icon matTooltip="{{'button.delete-tooltip' | translate}}" style="color: #B41E2A;">
                delete
              </mat-icon>
            </button>
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
      </table>
    } @else if(hasAdditionalCosts()) {
      <table mat-table [dataSource]="dataSource">
        <ng-container matColumnDef="costType">
          <th class="table-header" mat-header-cell *matHeaderCellDef style="width: 35%;">
            {{ 'budget-inclusion.costType' | translate }}
          </th>
          <td mat-cell *matCellDef="let element">
            {{ element.costType.costType }}
          </td>
        </ng-container>

        <ng-container matColumnDef="description">
          <th class="table-header" mat-header-cell *matHeaderCellDef style="width: 55%;">
            {{ 'budget-inclusion.description' | translate }}
          </th>
          <td mat-cell *matCellDef="let element">
            {{ element.description || '-' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="value">
          <th class="table-header" mat-header-cell *matHeaderCellDef style="width: 10%;">
            {{ 'budget-inclusion.costValue' | translate }}
          </th>
          <td mat-cell *matCellDef="let element">
            {{ element.value | number:'1.2-2' }}
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
      </table>
    }

    <div fxFlex="100" fxLayout="row" fxLayoutAlign="end">
      <p class="order-total-value">
        {{ 'budget-inclusion.orderTotal' | translate }} R${{ totalOrderValue | number:'1.2-2':'pt-BR' }}
      </p>
    </div>

    <h2 class="subtitle mg-b-22">{{ 'budget-inclusion.additionalInfo' | translate }}</h2>

    <form
      fxFlex="100"
      fxLayout="column"
      fxLayoutGap="20px"
      fxLayoutAlign="start"
      [formGroup]="additionalInfoForm"
    >
      <div fxFlex="100" fxLayout="row" fxLayoutGap="15px">
        <div fxFlex="100" fxLayout="row" fxLayoutGap="15px">
          @if(!isReadOnly) {
            <mat-form-field fxFlex="50" appearance="outline">
              <mat-label>{{ 'budget-inclusion.paymentTermDays' | translate }}</mat-label>
              <input
                (keydown)="allowOnlyDigits($event)"
                matInput
                formControlName="paymentTermDays"
                type="number"
                min="0"
              />
            </mat-form-field>
            <mat-form-field fxFlex="50" appearance="outline">
              <mat-label>{{ 'budget-inclusion.deliveryTermDays' | translate }}</mat-label>
              <input
                (keydown)="allowOnlyDigits($event)"
                matInput
                formControlName="deliveryTermDays"
                type="number"
                min="0"
              />
            </mat-form-field>
          } @else {
            <mat-form-field fxFlex="50" appearance="outline">
              <mat-label>{{ 'budget-inclusion.paymentTermDays' | translate }}</mat-label>
              <input
                matInput
                formControlName="paymentTermDays"
                [value]="additionalInfoForm.get('paymentTermDays')?.value || '-'"
                readonly
              />
            </mat-form-field>
            <mat-form-field fxFlex="50" appearance="outline">
              <mat-label>{{ 'budget-inclusion.deliveryTermDays' | translate }}</mat-label>
              <input
                matInput
                formControlName="deliveryTermDays"
                [value]="additionalInfoForm.get('deliveryTermDays')?.value || '-'"
                readonly
              />
            </mat-form-field>
          }
        </div>
        <mat-form-field fxFlex="50" appearance="outline">
          <mat-label>{{ 'budget-inclusion.budgetResponse' | translate }}</mat-label>
          <input
            formControlName="budgetResponseDate"
            name="budgetResponseDate"
            type="text"
            matInput
            [matDatepicker]="picker"
            [readonly]="isReadOnly"
          />
          <mat-datepicker-toggle matSuffix [for]="picker" [hidden]="isReadOnly"></mat-datepicker-toggle>
          <mat-datepicker [calendarHeaderComponent]="customHeader" #picker></mat-datepicker>
        </mat-form-field>
      </div>

      <div fxFlex="100">
        @if(!isReadOnly) {
          <mat-form-field fxFlex="100" appearance="outline">
            <mat-label>{{ 'budget-inclusion.budgetObservations' | translate }}</mat-label>
            <textarea matInput formControlName="budgetObservations" maxlength="1000"></textarea>
            <mat-hint align="end" class="count">
              {{ additionalInfoForm.get('budgetObservations')?.value?.length || 0 }} / 1000
            </mat-hint>
          </mat-form-field>
        } @else {
          <mat-form-field fxFlex="100" appearance="outline">
            <mat-label>{{ 'budget-inclusion.budgetObservations' | translate }}</mat-label>
            <textarea
              matInput
              formControlName="budgetObservations"
              [value]="additionalInfoForm.get('budgetObservations')?.value || '-'"
              readonly
            ></textarea>
          </mat-form-field>
        }
      </div>
    </form>

    <h2 class="subtitle mg-b-22" style="margin-top: 32px;">{{ 'budget-inclusion.files' | translate }}</h2>

    @if(!isReadOnly) {
      <form
        fxFlex="100"
        fxLayout="row"
        fxLayoutGap="20px"
        fxLayoutAlign="start center"
        [formGroup]="fileForm"
      >
        <mat-form-field fxFlex="50" appearance="outline">
          <mat-label>{{ 'budget-inclusion.description' | translate }}</mat-label>
          <input matInput type="text" formControlName="description" />
        </mat-form-field>

        <div fxFlex="100">
          <input type="file" class="file-input" (change)="onFileSelected($event)" #fileUpload />
          <mat-form-field
            fxFlex="100"
            appearance="outline"
            (click)="fileUpload.click()"
            [class.mat-form-field-invalid]="fileForm.controls['fileName'].invalid && fileForm.controls['fileName'].touched"
          >
            <mat-label>{{ 'budget-inclusion.file' | translate }}</mat-label>
            <input matInput type="text" formControlName="fileName" readonly />
            <mat-icon matSuffix>attach_file</mat-icon>
          </mat-form-field>
        </div>

        <button
          type="button"
          mat-flat-button
          color="primary"
          class="add-button mg-b-22"
          [disabled]="fileForm.invalid"
          (click)="addFile()"
        >
          {{ 'button.add' | translate }}
        </button>
      </form>
    }
    <table mat-table [dataSource]="filedataSource">
      <ng-container matColumnDef="uploadDate">
        <th class="table-header" mat-header-cell *matHeaderCellDef style="width: 40%;">
          {{ 'budget-inclusion.uploadDate' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element?.uploadDate | date: "dd/MM/yyyy - HH:mm" }}
        </td>
      </ng-container>

      <ng-container matColumnDef="description">
        <th class="table-header" mat-header-cell *matHeaderCellDef style="width: 20%;">
          {{ 'budget-inclusion.description' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.description || '-' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="file">
        <th class="table-header" mat-header-cell *matHeaderCellDef style="width: 20%;">
          {{ 'budget-inclusion.file' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element?.fileName }}
        </td>
      </ng-container>

      <ng-container matColumnDef="action">
        <th class="table-header action" mat-header-cell *matHeaderCellDef style="width: 40%;">
          {{ 'budget-inclusion.action' | translate }}
        </th>

        <td mat-cell *matCellDef="let element" class="action">
          <button [disabled]="!element.id" mat-icon-button class="download-button" (click)="downloadFile(element)">
            <mat-icon matTooltip="{{ 'button.download-tooltip' | translate }}">
              file_download
            </mat-icon>
          </button>
          @if(!isReadOnly) {
            <button mat-icon-button (click)="deleteFile(element)">
              <mat-icon matTooltip="{{'button.delete-tooltip' | translate}}" style="color: #B41E2A;">
                delete
              </mat-icon>
            </button>
          }
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="displayedColumnsFiles;"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumnsFiles;"></tr>
    </table>
    <app-breadcrumbs [breadcrumbsData]="breadcrumbsData"></app-breadcrumbs>
  </div>
</div>
